import { FC, ReactNode } from 'react';
import {
  Box,
  Button,
  Stack,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from '@ui/icons';
import { useGetSpacing } from '@ui';

type Props = {
  title?: string;
  additionalTitleComponent?: ReactNode;
  onClose?: (
    event: object,
    reason: 'closeButton' | 'backdropClick' | 'escapeKeyDown'
  ) => void;
  closeTitle?: string;
  onNext?: () => void;
  nextDisabled?: boolean;
  nextTitle?: string;
  sx?: React.CSSProperties;
};

export const ViewNavigationBar: FC<Props> = ({
  onClose,
  closeTitle,
  title,
  additionalTitleComponent,
  onNext,
  nextDisabled = false,
  nextTitle = 'Next',
  sx = {},
}) => {
  const getSpacing = useGetSpacing();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{
          boxSizing: 'border-box',
          alignItems: 'center',
          display: 'inline-block',
          height: {
            xs: 64,
            sm: 64,
            md: 72,
          },
          position: 'relative',
          width: '100%',
          zIndex: 5,
          backgroundColor: '#FFFFFF',
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            alignContent: 'stretch',
            justifyContent: 'center',
            alignSelf: 'auto',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            flexGrow: 0,
            position: 'absolute',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              width: 'calc(100% - 151.9375px)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Typography
                sx={{ fontSize: 18, fontWeight: 400, color: '#0032A0' }}
              >
                {title}
              </Typography>
              {additionalTitleComponent}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            float: 'left',
            alignItems: 'center',
            display: 'flex',
            flexBasis: {
              xs: 64,
              sm: 64,
              md: 72,
            },
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            paddingLeft: getSpacing('xl'),
          }}
        >
          {onClose &&
            (isSmallScreen ? (
              <IconButton
                aria-label="close"
                onClick={(event) => onClose(event, 'closeButton')}
                color="primary"
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                aria-label="close"
                onClick={() => onClose({}, 'closeButton')}
              >
                {closeTitle ? closeTitle : t('cancel')}
              </Button>
            ))}
        </Box>

        <Box
          sx={{
            float: 'right',
            alignItems: 'center',
            display: 'flex',
            flexBasis: {
              xs: 64,
              sm: 64,
              md: 72,
            },
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            paddingRight: getSpacing('xl'),
          }}
        >
          {onNext && (
            <Button
              variant="contained"
              disabled={nextDisabled}
              onClick={() => onNext()}
            >
              {nextTitle}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
